import React, { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlaceIcon from "@mui/icons-material/Place";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  searchCustomers,
  fetchCustomerAddresses,
} from "../Misc/utilities";

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  required,
  hint,
}) => (
  <label className="block">
    <span className="mb-1 block text-sm font-medium">
      {label}
      {required && (
        <span className="ml-0.5 text-rose-500 dark:text-rose-400">*</span>
      )}
    </span>
    <input
      type="text"
      value={value || ""}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="app-input w-full rounded-xl border px-3 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
    />
    {hint && <span className="app-muted mt-1 block text-xs">{hint}</span>}
  </label>
);

/*
  Step 1 — who the quote is for.

  Search is its own field here rather than a magnifying glass bolted onto the
  customer-name input, and results/addresses land inline instead of in a chain
  of dialogs. Picking a customer marks imported_customer immediately (the old
  flow only set it once an address was also chosen, so the escape hatch for a
  wrong pick never appeared if you dismissed the address dialog).
*/
const CustomerStep = ({ formData, setFormData }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = nothing searched yet
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [addresses, setAddresses] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressError, setAddressError] = useState("");

  const imported = !!formData.imported_customer;

  const update = (patch) => setFormData((prev) => ({ ...prev, ...patch }));

  const runSearch = async () => {
    const term = query.trim();
    if (term.length < 2) {
      setSearchError("Enter at least 2 characters to search.");
      setResults(null);
      return;
    }
    setSearchError("");
    setResults(null);
    setSearching(true);
    try {
      setResults(await searchCustomers(term));
    } catch (error) {
      setSearchError(
        "Couldn't reach Bistrack. Try again, or fill in the details below manually."
      );
    } finally {
      setSearching(false);
    }
  };

  const chooseCustomer = async (record) => {
    update({
      account: record.CustomerID,
      customer: record.Name,
      imported_customer: true,
    });
    setQuery(record.Name);
    setResults(null);
    setAddresses(null);
    setAddressError("");
    setLoadingAddresses(true);
    try {
      setAddresses(await fetchCustomerAddresses(record.CustomerID));
    } catch (error) {
      setAddressError(
        "Couldn't load addresses for this customer — type one in below."
      );
    } finally {
      setLoadingAddresses(false);
    }
  };

  const chooseAddress = (record) => {
    update({
      address: record.FormatedAddress,
      // Bistrack prefixes each address label with the job number.
      job: (record.CustomerAddress || "").split(" ")[0],
    });
    setAddresses(null);
  };

  const clearCustomer = () => {
    update({
      customer: "",
      account: "",
      job: "",
      address: "",
      imported_customer: false,
    });
    setQuery("");
    setResults(null);
    setAddresses(null);
    setSearchError("");
    setAddressError("");
  };

  const showAddressPanel =
    loadingAddresses || !!addressError || addresses !== null;

  return (
    <div className="space-y-5">
      {imported ? (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2.5">
              <CheckCircleIcon
                className="mt-0.5 shrink-0 text-emerald-500"
                sx={{ fontSize: 20 }}
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">
                  {formData.customer}
                </p>
                <p className="app-muted text-xs">
                  Pulled from Bistrack
                  {formData.account ? ` · Account #${formData.account}` : ""}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={clearCustomer}
              className="app-btn app-btn-secondary app-btn-sm shrink-0"
            >
              Change customer
            </button>
          </div>
        </div>
      ) : (
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="rfq-search">
            Search Bistrack
          </label>
          <div className="flex flex-wrap gap-2">
            <div className="relative min-w-[200px] flex-1">
              <SearchIcon
                fontSize="small"
                className="app-muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
              />
              <input
                id="rfq-search"
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") runSearch();
                }}
                placeholder="Customer name…"
                className="app-input w-full rounded-xl border py-2.5 pl-9 pr-3 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={runSearch}
              disabled={searching}
              className="app-btn app-btn-primary"
            >
              {searching ? "Searching…" : "Search"}
            </button>
          </div>

          {searchError && (
            <p className="mt-2 flex items-start gap-1.5 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
              <ErrorOutlineIcon sx={{ fontSize: 16 }} className="mt-0.5" />
              {searchError}
            </p>
          )}

          {searching && (
            <div className="app-surface mt-3 flex items-center gap-2.5 rounded-xl border p-3 text-sm">
              <CircularProgress size={16} />
              <span className="app-muted">Searching Bistrack…</span>
            </div>
          )}

          {!searching && results && results.length === 0 && (
            <div className="app-surface mt-3 rounded-xl border p-4 text-center">
              <p className="text-sm font-medium">
                No customers matched “{query.trim()}”
              </p>
              <p className="app-muted mt-1 text-xs">
                Check the spelling, or enter the details manually below.
              </p>
            </div>
          )}

          {!searching && results && results.length > 0 && (
            <>
              <p className="app-muted mt-3 text-xs font-semibold uppercase tracking-wider">
                {results.length} match{results.length === 1 ? "" : "es"}
              </p>
              <ul className="app-surface mt-1.5 max-h-72 divide-y divide-[color:var(--app-divider)] overflow-y-auto rounded-xl border">
                {results.map((record, index) => (
                  <li key={`${record.CustomerID}-${index}`}>
                    <button
                      type="button"
                      onClick={() => chooseCustomer(record)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[color:var(--app-hover)]"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">
                          {record.Name}
                        </span>
                        <span className="app-muted block text-xs">
                          Account #{record.CustomerID}
                        </span>
                      </span>
                      <ArrowForwardIcon
                        className="app-muted shrink-0"
                        sx={{ fontSize: 18 }}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {showAddressPanel && (
        <div className="app-surface rounded-xl border p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold">Delivery address</p>
            {!loadingAddresses && (
              <button
                type="button"
                onClick={() => {
                  setAddresses(null);
                  setAddressError("");
                }}
                className="app-muted rounded-full px-2 py-1 text-xs font-medium hover:bg-[color:var(--app-hover)] hover:text-[color:var(--app-text)]"
              >
                Skip for now
              </button>
            )}
          </div>

          {loadingAddresses && (
            <div className="mt-2 flex items-center gap-2.5 text-sm">
              <CircularProgress size={16} />
              <span className="app-muted">Loading addresses…</span>
            </div>
          )}

          {addressError && (
            <p className="mt-2 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
              {addressError}
            </p>
          )}

          {!loadingAddresses && addresses && addresses.length === 0 && (
            <p className="app-muted mt-2 text-sm">
              No addresses on file for this customer — type one in below.
            </p>
          )}

          {!loadingAddresses && addresses && addresses.length > 0 && (
            <ul className="app-card mt-2 divide-y divide-[color:var(--app-divider)] overflow-hidden rounded-xl border">
              {addresses.map((record, index) => (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => chooseAddress(record)}
                    className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left text-sm transition hover:bg-[color:var(--app-hover)]"
                  >
                    <PlaceIcon
                      className="app-muted mt-0.5 shrink-0"
                      sx={{ fontSize: 16 }}
                    />
                    <span className="min-w-0">{record.CustomerAddress}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm font-semibold">Customer details</p>
          {!imported && (
            <p className="app-muted text-xs">
              No Bistrack account? Fill these in directly.
            </p>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Customer name"
            required
            value={formData.customer}
            disabled={imported}
            onChange={(value) => update({ customer: value })}
            placeholder="e.g. Acme Builders"
            hint={
              imported
                ? "Locked to the Bistrack record — use “Change customer” to edit."
                : undefined
            }
          />
          <Field
            label="Account number"
            value={formData.account}
            onChange={(value) => update({ account: value })}
            placeholder="e.g. 10428"
          />
          <Field
            label="Job number"
            value={formData.job}
            onChange={(value) => update({ job: value })}
            placeholder="e.g. 1042"
          />
          <Field
            label="Delivery address"
            value={formData.address}
            onChange={(value) => update({ address: value })}
            placeholder="Street, city, state"
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerStep;
